// „Neuer Mieter“ als Drehkarte (Handoff Mieter §5.2): Entwurf, Prüfung, Hinweise, Kaution,
// Mieterwechsel-Datum und die Daten für den mieter-Insert. Reine Funktionen – testbar.

import { leseZahl } from '../immobilien/mietverhaeltnisFormular';
import { emailGueltig } from '../immobilien/portal';
import { eur } from '../immobilien/format';
import { deDatum } from '../immobilien/objektDetail';
import { mitAnfangsstand, spiegelFelder, vortag } from './mietstaende';
import type { KontaktKanal } from '../kontakt/kontaktKanaele';
import { istWohnend } from '../dashboard/leerstand';
import { mieteAktuell } from '../immobilien/einheitDetail';
import { mieterName, type MieterDetailRoh } from '../immobilien/objektDetail';

export type Anrede = 'Frau' | 'Herr' | 'Eheleute' | 'Divers';
export const ANREDEN: readonly Anrede[] = ['Frau', 'Herr', 'Eheleute', 'Divers'];

/** Schreibweise wie in den Daten und im bisherigen Formular. */
export type Zahlungsweise = 'Überweisung' | 'Lastschrift' | 'Dauerauftrag';
export const ZAHLUNGSWEISEN: readonly Zahlungsweise[] = ['Überweisung', 'Lastschrift', 'Dauerauftrag'];

/** Belegte Einheit: Mieterwechsel (Vormieter zieht aus) oder Mitmieter (zieht zusätzlich ein). */
export type EinzugsArt = 'wechsel' | 'mitmieter';

/** § 551 BGB: höchstens drei Monatskaltmieten. */
export const KAUTION_MAX_FAKTOR = 3;

export interface NeuerMieterEntwurf {
  anrede: Anrede;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  email: string;
  telefon: string;
  objektId: string | null;
  einheitId: string | null;
  art: EinzugsArt;
  mietbeginn: string;
  mietende: string;
  kalt: string;
  nk: string;
  hk: string;
  iban: string;
  kontoinhaber: string;
  kautionFaktor: number;
  zahlungsweise: Zahlungsweise;
}

export const LEERER_ENTWURF: NeuerMieterEntwurf = {
  anrede: 'Frau', vorname: '', nachname: '', geburtsdatum: '', email: '', telefon: '',
  objektId: null, einheitId: null, art: 'wechsel',
  mietbeginn: '', mietende: '', kalt: '', nk: '', hk: '', iban: '', kontoinhaber: '',
  kautionFaktor: KAUTION_MAX_FAKTOR, zahlungsweise: 'Überweisung',
};

const betrag = (v: string): number => leseZahl(v) ?? 0;
const runde = (n: number): number => Math.round(n * 100) / 100;

export function kautionFaktor(f: number): number {
  return Math.min(KAUTION_MAX_FAKTOR, Math.max(0, Math.round(f)));
}

/** Kaution = Faktor (0–3) × Kaltmiete. */
export function kautionBetrag(kalt: number, faktor: number): number {
  return runde(Math.max(0, kalt) * kautionFaktor(faktor));
}

/** „3 × Kaltmiete · 2.460 €“ */
export function kautionLabel(e: Pick<NeuerMieterEntwurf, 'kalt' | 'kautionFaktor'>): string {
  return `${kautionFaktor(e.kautionFaktor)} × Kaltmiete · ${eur(kautionBetrag(betrag(e.kalt), e.kautionFaktor), { nachkomma: 0 })}`;
}

export function gesamtmiete(e: Pick<NeuerMieterEntwurf, 'kalt' | 'nk' | 'hk'>): number {
  return runde(betrag(e.kalt) + betrag(e.nk) + betrag(e.hk));
}

/** Beim Mieterwechsel endet das Mietverhältnis des Vormieters am Tag vor dem Einzug. */
export function mietendeVormieter(mietbeginn: string): string {
  return vortag(mietbeginn);
}

export function schritteErledigt(e: NeuerMieterEntwurf): [boolean, boolean, boolean] {
  return [
    !!e.vorname.trim() && !!e.nachname.trim(),
    !!e.objektId && !!e.einheitId,
    !!e.mietbeginn && betrag(e.kalt) > 0,
  ];
}

/** Fehler in ausgefüllten Feldern (leere optionale Felder sind kein Fehler). */
export function feldFehler(e: NeuerMieterEntwurf): Partial<Record<'email' | 'mietende' | 'geburtsdatum' | 'iban', string>> {
  const f: Partial<Record<'email' | 'mietende' | 'geburtsdatum' | 'iban', string>> = {};
  if (e.email.trim() && !emailGueltig(e.email)) f.email = 'Bitte eine gültige E-Mail-Adresse eingeben';
  if (e.mietende && e.mietbeginn && e.mietende < e.mietbeginn) f.mietende = 'Mietende liegt vor dem Mietbeginn';
  if (e.geburtsdatum && e.mietbeginn && e.geburtsdatum >= e.mietbeginn) f.geburtsdatum = 'Geburtsdatum liegt nach dem Mietbeginn';
  const iban = e.iban.replace(/\s+/g, '');
  if (iban && !/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/i.test(iban)) f.iban = 'IBAN ist unvollständig';
  return f;
}

/** Hinweis in der Fußzeile (Texte wie im Prototyp). */
export function hinweis(e: NeuerMieterEntwurf): { text: string; bereit: boolean } {
  const [s1, s2, s3] = schritteErledigt(e);
  if (!s1) return { text: 'Noch offen: Vor- und Nachname', bereit: false };
  if (!s2) return { text: 'Noch offen: Objekt und Einheit', bereit: false };
  if (!s3) return { text: 'Noch offen: Mietbeginn und Kaltmiete', bereit: false };
  if (Object.keys(feldFehler(e)).length > 0) return { text: 'Bitte die markierten Felder prüfen', bereit: false };
  return { text: 'Alles bereit – Mieter kann angelegt werden', bereit: true };
}

/** Text unter „Mieterwechsel“: „Anna Beck zieht aus, Mietverhältnis endet am 31.10.2026.“ */
export function wechselText(vormieter: string, mietbeginn: string): string {
  return mietbeginn
    ? `${vormieter} zieht aus, Mietverhältnis endet am ${deDatum(mietendeVormieter(mietbeginn))}.`
    : `${vormieter} zieht aus, Mietverhältnis endet am Tag vor Einzug.`;
}

export interface MieteTeileEingabe { kalt: number; nk: number; hk: number }

/** Belegte Einheit gewählt: Kalt/NK/Heizung vom Vormieter vorbelegen – nur leere Felder. */
export function mitVorbelegung(e: NeuerMieterEntwurf, miete: MieteTeileEingabe | null): NeuerMieterEntwurf {
  if (!miete) return e;
  const text = (n: number) => (n > 0 ? String(n).replace('.', ',') : '');
  return {
    ...e,
    kalt: e.kalt.trim() ? e.kalt : text(miete.kalt),
    nk: e.nk.trim() ? e.nk : text(miete.nk),
    hk: e.hk.trim() ? e.hk : text(miete.hk),
  };
}

const leer = (s: string): string | null => (s.trim() ? s.trim() : null);

/**
 * Daten für den mieter-Insert. E-Mail und Telefon laufen über die Kontaktkanäle
 * (speichereKanaele spiegelt sie in die mieter-Zeile). Mietstand ab Mietbeginn in miet_historie.
 */
export function insertPayload(e: NeuerMieterEntwurf, einheitBezeichnung: string): Record<string, unknown> {
  const kalt = betrag(e.kalt);
  const werte = { kaltmiete: kalt, nebenkosten_vorauszahlung: betrag(e.nk), heizkosten: betrag(e.hk), weitere_kosten: 0 };
  const historie = mitAnfangsstand([], werte, e.mietbeginn);
  const stand = historie[0];
  const name = `${e.vorname.trim()} ${e.nachname.trim()}`;
  const inhaber = e.kontoinhaber.trim();
  return {
    anrede: e.anrede,
    vorname: e.vorname.trim(),
    nachname: e.nachname.trim(),
    geburtsdatum: e.geburtsdatum || null,
    objekt_id: e.objektId,
    einheit_id: e.einheitId,
    einheit: einheitBezeichnung || null,
    status: 'aktiv',
    mietbeginn: e.mietbeginn,
    mietende: e.mietende || null,
    ...(stand ? spiegelFelder(stand) : werte),
    miet_historie: historie,
    kaution: kautionBetrag(kalt, e.kautionFaktor),
    zahlungsweise: e.zahlungsweise,
    mieter_iban: leer(e.iban.replace(/\s+/g, ' ')),
    // Kontoinhaber nur, wenn abweichend vom Mieter
    mieter_kontoinhaber: inhaber && inhaber !== name ? inhaber : null,
  };
}

export function kanaeleAus(e: Pick<NeuerMieterEntwurf, 'email' | 'telefon'>): KontaktKanal[] {
  const k: KontaktKanal[] = [];
  if (e.email.trim()) k.push({ kanal: 'email', wert: e.email.trim(), label: null, ist_primaer: true, sortierung: 0 });
  if (e.telefon.trim()) k.push({ kanal: 'telefon', wert: e.telefon.trim(), label: null, ist_primaer: true, sortierung: 0 });
  return k;
}

export interface EinheitBelegung {
  id: string;
  bezeichnung: string;
  /** wohnende Mieter (aktiv/gekündigt), deren Mietverhältnis heute läuft */
  bewohner: { id: string; name: string; nachname: string }[];
  /** Miete der Bewohner zur Vorbelegung */
  miete: MieteTeileEingabe | null;
}

/** Einheiten eines Objekts mit Belegung (frei / belegt · Nachname), sortiert nach Bezeichnung. */
export function einheitenMitBelegung(
  einheiten: readonly { id: string; bezeichnung: string | null; objekt_id: string | null }[],
  mieter: readonly (MieterDetailRoh & { objekt_id: string | null })[],
  objektId: string,
  heute: string,
): EinheitBelegung[] {
  const sortierer = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });
  return einheiten
    .filter(x => x.objekt_id === objektId)
    .map(x => {
      const laufend = mieter.filter(m => m.einheit_id === x.id && istWohnend(m.status)
        && (!m.mietbeginn || m.mietbeginn.slice(0, 10) <= heute)
        && (!m.mietende || m.mietende.slice(0, 10) >= heute));
      const miete = laufend.length ? mieteAktuell(laufend, heute) : null;
      return {
        id: x.id,
        bezeichnung: (x.bezeichnung ?? '').trim() || 'Ohne Bezeichnung',
        bewohner: laufend.map(m => ({ id: m.id, name: mieterName(m), nachname: (m.firma_name ?? '').trim() || (m.nachname ?? '').trim() || mieterName(m) })),
        miete: miete ? { kalt: miete.kalt, nk: miete.nk, hk: miete.hk } : null,
      };
    })
    .sort((a, b) => sortierer.compare(a.bezeichnung, b.bezeichnung));
}
